import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import { fireOperationalAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { getEscalatorBaseUrl } from '@atlassian/jira-servicedesk-incident-management-common/src/utils';
import type { Action } from '@atlassian/react-sweet-state';
import messages from '../../../common/messages';
import type { ContainerProps, State } from '../../types';
import { fetchSlackWorkspaceConnectionsForProject } from '../fetch-slack-workspace-connections';

type ConnectSlackWorkspaceArgs = {
	connectionId: string;
	projectId?: string;
};

export const connectSlackToProject =
	({ connectionId, projectId }: ConnectSlackWorkspaceArgs): Action<State, ContainerProps> =>
	async ({ getState, dispatch, setState }, { cloudId, analyticsEvent }) => {
		const { slackWorkspaceConnections } = getState();
		const { showFlag } = useFlagsService();

		if (slackWorkspaceConnections.isLoading) {
			return;
		}

		setState({
			slackWorkspaceConnections: {
				...slackWorkspaceConnections,
				isLoading: true,
			},
		});

		try {
			if (projectId == null) {
				throw new Error('projectId is not set');
			}
			await performPostRequest(
				`${getEscalatorBaseUrl(
					cloudId,
				)}/chat/connection/${projectId}?connectionId=${encodeURIComponent(connectionId)}`,
			);

			setState({
				slackWorkspaceConnections: {
					...slackWorkspaceConnections,
					data: slackWorkspaceConnections.data?.map((connection) =>
						connection.connectionId === connectionId
							? {
									...connection,
									enabled: true,
								}
							: connection,
					),
					isLoading: false,
				},
			});

			fireOperationalAnalytics(analyticsEvent, 'connectSlackToProject succeeded');

			dispatch(
				fetchSlackWorkspaceConnectionsForProject({
					cloudId,
					projectId,
					analyticsEvent,
				}),
			);

			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			showFlag({
				type: 'error',
				title: messages.connectionToProjectErrorFlagText,
			});
			setState({
				slackWorkspaceConnections: {
					...slackWorkspaceConnections,
					isLoading: false,
				},
			});
			fireErrorAnalytics({
				meta: {
					id: 'connectSlackToProject',
					packageName: 'jiraServicedeskIncidentManagementSettings',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	};
